import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AdminUserResponse, PermissionDetails } from '@solverml/api';
import { AdminDataService } from '../../controller/admin-data.service';
import { WiseIdAuthServiceManager } from "../../../sso-wise-id/controller/wise-id-auth.service-manager";

@Component({
  selector: 'app-permission-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSnackBarModule],
  templateUrl: './permission-editor.component.html',
  styleUrls: ['./permission-editor.component.scss']
})
export class PermissionEditorComponent implements OnChanges {

  @Input() selectedUser: AdminUserResponse | null = null;
  @Output() permissionsSaved = new EventEmitter<void>();
  @Output() userDeleted = new EventEmitter<void>();

  public permissions: PermissionDetails | null = null;
  public activeAccordion: string | null = null;
  public canAssignAdmin: boolean = false;

  public isDeleting = signal<boolean>(false);

  constructor(
    private adminDataService: AdminDataService,
    private authService: WiseIdAuthServiceManager,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    const currentUserProfile = this.authService.getUserProfileSessionData();
    if (currentUserProfile) {
      this.canAssignAdmin = currentUserProfile.permission_access.canAssignAdmin;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedUser'] && this.selectedUser) {
      this.permissions = JSON.parse(JSON.stringify(this.selectedUser.permissions));

      if (this.permissions && this.permissions.categories) {
        this.permissions.categories.sort((a, b) => {
          const hierarchyA = a.hierarchy ?? 999;
          const hierarchyB = b.hierarchy ?? 999;
          return hierarchyA - hierarchyB;
        });
      }
      this.activeAccordion = null;
    } else if (changes['selectedUser'] && !this.selectedUser) {
      this.permissions = null;
    }
  }

  /**
   * Ejecuta la eliminación física del usuario.
   */
  public onDeleteUser(): void {
    if (!this.selectedUser) return;

    const confirmed = confirm(`¿Estás seguro de que deseas eliminar permanentemente a ${this.selectedUser.name}? Esta acción no se puede deshacer.`);
    if (!confirmed) return;

    this.isDeleting.set(true);
    this.adminDataService.deleteUser(this.selectedUser.idHash).subscribe({
      next: () => {
        this.snackBar.open('Usuario eliminado correctamente', 'Cerrar', { duration: 3000 });
        this.userDeleted.emit(); // Notificamos al padre para limpiar selección
        this.isDeleting.set(false);
      },
      error: (err) => {
        this.snackBar.open('Error al eliminar el usuario', 'Cerrar', { duration: 5000 });
        this.isDeleting.set(false);
        console.error('Error al borrar usuario:', err);
      }
    });
  }

  onSaveChanges(): void {
    if (this.selectedUser && this.permissions) {
      this.adminDataService.updateUserPermissions(this.selectedUser.idHash, this.permissions)
        .subscribe({
          next: () => {
            this.snackBar.open('Permisos actualizados correctamente', 'OK', { duration: 3000 });
            this.permissionsSaved.emit();
          },
          error: (err) => {
            this.snackBar.open('Error al guardar cambios', 'Cerrar', { duration: 5000 });
            console.error('Error al guardar los permisos', err);
          }
        });
    }
  }

  toggleAccordion(sectionId: string): void {
    this.activeAccordion = this.activeAccordion === sectionId ? null : sectionId;
  }
}
